import { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
    CheckCircle2Icon,
    CircleAlertIcon,
    ClockIcon,
    CloudUploadIcon,
    DownloadIcon,
    HelpCircleIcon,
    UploadIcon,
    XCircleIcon,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useHelper } from '@/context/HelpSidebarContext';
import { useFetch } from '@/hooks/useFecth';
import useClubImportsSocket from '@/hooks/useClubImportsSocket';

const IMPORT_STATUS = {
    CONCLUDED: { label: 'Concluída', variant: 'default', icon: CheckCircle2Icon },
    ERROR: { label: 'Erro', variant: 'destructive', icon: XCircleIcon },
    PROCESSING: { label: 'Processando', variant: 'outline', icon: ClockIcon },
    PENDING: { label: 'Pendente', variant: 'secondary', icon: CloudUploadIcon },
};

function ImportHelpContent() {
    return (
        <div className="flex flex-col gap-4 text-sm">
            <h2 className="text-base font-semibold">Importação de contatos</h2>
            <p>
                Após enviar a planilha, acompanhe os contadores neste dialog (atualização em tempo real). Ao fechar, o
                histórico da página é atualizado. Você também recebe e-mail ao concluir.
            </p>

            <div>
                <h3 className="mb-2 font-semibold">Instruções importantes</h3>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>Use sempre o arquivo modelo fornecido</li>
                    <li>
                        <strong>Não altere</strong> os nomes das colunas na tabela
                    </li>
                    <li>Mantenha o formato .xlsx do arquivo</li>
                    <li>
                        A senha provisória do assinante são os <strong>6 primeiros dígitos do CPF/CNPJ</strong>
                    </li>
                    <li>Cada assinante cadastrado recebe um e-mail de boas-vindas com os dados de acesso</li>
                    <li>
                        A linha de exemplo do modelo usa o domínio <strong>@example.com</strong> e é ignorada na
                        importação
                    </li>
                    <li>
                        Para vincular um dependente, preencha <strong>cpf_titular</strong> com o CPF do titular (mesmo
                        clube). O dependente nasce com assinatura local inativa e sem plano; o plano vigente é o do
                        titular (app/web). CPF inválido em <strong>cpf_titular</strong> não vira titular — a linha é
                        rejeitada.
                    </li>
                    <li>
                        Se algum dependente da família for inválido, o titular e os demais dependentes daquela família
                        também são rejeitados (nada parcial).
                    </li>
                    <li>
                        O limite de dependentes é o do plano do titular. E-mail pode se repetir entre linhas. Dependente
                        não recebe benefícios do kit.
                    </li>
                    <li>Para clubes que não vendem, o SKU do plano deve ser informado no titular (não no dependente)</li>
                    <li>
                        Em clubes que vendem, a coluna <strong>ciclo</strong> (mensal ou anual) define o ciclo do pacote
                        do titular junto com o SKU. Vazia: cadastro novo entra mensal e o existente mantém o ciclo
                        atual. Anual exige valor anual no plano.
                    </li>
                    <li>
                        Para quem já pagou fora da plataforma (ex.: vendido direto no Asaas, à vista ou parcelado),
                        preencha <strong>fim_ciclo</strong> (DD/MM/AAAA) com o fim do período pago, junto com SKU e
                        ciclo. O assinante entra no pacote em dinheiro com acesso até essa data; depois renova pelo
                        site. Vazio: o ciclo conta a partir da importação.
                    </li>
                </ul>
            </div>

            <div>
                <h3 className="mb-2 font-semibold">Processamento e erros</h3>
                <p className="text-muted-foreground">
                    Linhas com erro geram planilha de correções (mesmo modelo + coluna erro). Corrija e reenvie. A
                    planilha fica disponível neste dialog ao concluir e no histórico após fechar.
                </p>
            </div>

            <div>
                <h3 className="mb-2 font-semibold">Notificações</h3>
                <p className="mb-2 text-muted-foreground">Você receberá um e-mail confirmando:</p>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>Sucesso na importação</li>
                    <li>Erros encontrados (se houver)</li>
                    <li>Quantidade de contatos importados</li>
                </ul>
            </div>
        </div>
    );
}

function ImportStatusBadge({ status }) {
    const config = IMPORT_STATUS[status] || {
        label: status || 'Desconhecido',
        variant: 'outline',
        icon: ClockIcon,
    };
    const Icon = config.icon;

    return (
        <Badge variant={config.variant} className="gap-1">
            <Icon className="size-3" />
            {config.label}
        </Badge>
    );
}

function KpiCell({ label, value, className }) {
    return (
        <div className={cn('rounded-lg border p-3 text-center', className)}>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums">{value ?? 0}</p>
        </div>
    );
}

function ImportProgressPanel({ importItem, connected }) {
    const total = Number(importItem.countTotal) || 0;
    const processed = Number(importItem.countProcessed) || 0;
    const success = Number(importItem.countSuccess) || 0;
    const errors = Number(importItem.countError) || 0;
    const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
    const isActive = importItem.status === 'PENDING' || importItem.status === 'PROCESSING';
    const isDone = importItem.status === 'CONCLUDED' || importItem.status === 'ERROR';

    return (
        <div className="flex flex-col gap-4">
            <Alert variant={isDone && errors > 0 ? 'destructive' : 'default'}>
                <CircleAlertIcon />
                <AlertTitle>
                    {isActive ? 'Importação em andamento' : errors > 0 ? 'Importação concluída com erros' : 'Importação concluída'}
                </AlertTitle>
                <AlertDescription>
                    {isActive
                        ? 'Acompanhe os contadores abaixo. Nova importação só após concluir esta.'
                        : errors > 0
                          ? 'Baixe a planilha de erros, corrija e envie novamente.'
                          : 'Todos os registros válidos foram processados.'}
                </AlertDescription>
            </Alert>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <ImportStatusBadge status={importItem.status} />
                <span className="text-xs text-muted-foreground">
                    {connected ? 'Atualizando ao vivo' : isActive ? 'Reconectando…' : 'Finalizado'}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <KpiCell label="Total" value={total} />
                <KpiCell label="Processados" value={processed} />
                <KpiCell label="Sucesso" value={success} className="border-emerald-200/80" />
                <KpiCell label="Erros" value={errors} className={errors > 0 ? 'border-destructive/40' : undefined} />
            </div>

            <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Progresso</span>
                    <span className="tabular-nums">{percent}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                        className={cn(
                            'h-full rounded-full transition-[width] duration-300',
                            isDone && errors > 0 ? 'bg-destructive' : 'bg-primary',
                        )}
                        style={{ width: `${Math.max(percent, isActive && importItem.status === 'PENDING' ? 2 : 0)}%` }}
                    />
                </div>
            </div>

            {importItem.url ? (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    render={<a href={importItem.url} target="_blank" rel="noopener noreferrer" />}
                >
                    <DownloadIcon className="size-4" />
                    Baixar planilha de erros
                </Button>
            ) : null}
        </div>
    );
}

export default function ClubAddContacts({ onImport, loading, clubData, onClose }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [liveImport, setLiveImport] = useState(null);
    const { chargeHelp } = useHelper();
    const fileInputRef = useRef(null);

    const { data: imports, mutate: mutateActive } = useFetch('club/subscribers/import', {
        params: { clubId: clubData?.id },
    });

    const importRows = Array.isArray(imports) ? imports : [];
    const hasActiveImports = importRows.some(item => item.status === 'PENDING' || item.status === 'PROCESSING');
    const tracking = liveImport || importRows[0] || null;
    const showProgress = Boolean(tracking);
    const hasCycle = Boolean(clubData?.functions?.includes('reseller'));
    const hasSkuPlan = Boolean(clubData?.functions?.includes('benefit')) || hasCycle;
    const { data: plans } = useFetch(hasSkuPlan ? 'club/settings/plans' : null);

    useEffect(() => {
        if (!open) return;
        const active = importRows.find(item => item.status === 'PENDING' || item.status === 'PROCESSING');
        if (active) {
            setLiveImport(prev => {
                if (!prev || Number(prev.id) !== Number(active.id)) return active;
                return prev;
            });
        }
    }, [open, importRows]);

    const onSocketUpdate = useCallback(payload => {
        const row = payload?.import;
        if (!row?.id) return;
        setLiveImport(prev => {
            if (prev && Number(prev.id) !== Number(row.id)) return prev;
            return row;
        });
    }, []);

    const { connected } = useClubImportsSocket(clubData?.id, {
        enabled: open && Boolean(clubData?.id),
        revalidateSWR: false,
        onUpdate: onSocketUpdate,
    });

    const handleOpenChange = nextOpen => {
        if (loading && !nextOpen) return;
        setOpen(nextOpen);
        if (!nextOpen) {
            setFile(null);
            setLiveImport(null);
            onClose?.();
        }
    };

    const handleFileChange = event => {
        if (hasActiveImports || (liveImport && ['PENDING', 'PROCESSING'].includes(liveImport.status))) return;

        const uploadedFile = event.target.files?.[0];
        if (uploadedFile?.name.endsWith('.xlsx')) {
            setFile(uploadedFile);
        } else {
            setFile(null);
        }
    };

    const handleImport = async () => {
        if (!file || hasActiveImports || loading) return;
        const summary = await onImport?.({ file });
        setFile(null);
        if (summary?.importId) {
            setLiveImport({
                id: summary.importId,
                status: Number(summary.ready) > 0 ? 'PENDING' : 'CONCLUDED',
                countTotal: summary.total ?? 0,
                countProcessed: Number(summary.ready) > 0 ? Number(summary.errors) || 0 : Number(summary.total) || 0,
                countSuccess: 0,
                countError: summary.errors ?? 0,
                url: summary.errorUrl || null,
            });
        }
        await mutateActive();
    };

    const handleDownloadTemplate = () => {
        const headers = [
            'nome',
            'email',
            'CPF',
            'cpf_titular',
            'data_nascimento',
            'telefone',
            'genero',
            'cep',
            'endereco',
            'numero',
            'complemento',
            'bairro',
            'cidade',
            'estado',
            'pais',
            ...(hasSkuPlan ? ['sku'] : []),
            ...(hasCycle ? ['ciclo', 'fim_ciclo'] : []),
        ];
        const sampleSku = Array.isArray(plans)
            ? plans.find(item => item?.status !== false && item?.sku)?.sku || plans.find(item => item?.sku)?.sku
            : undefined;
        const sampleRow = [
            'Maria da Silva',
            'maria.silva.exemplo@example.com',
            '52998224725',
            '',
            new Date(1990, 4, 20, 12),
            '11987654321',
            'feminino',
            '01310100',
            'Avenida Paulista',
            '1000',
            'Apto 101',
            'Bela Vista',
            'São Paulo',
            'SP',
            'Brasil',
            ...(hasSkuPlan ? [sampleSku || 'SUBSTITUA_PELO_SKU_DO_PLANO'] : []),
            ...(hasCycle ? ['mensal', ''] : []),
        ];
        const emptyRow = headers.map(() => '');
        const rows = [headers, sampleRow, ...Array(999).fill(null).map(() => [...emptyRow])];
        const ws = XLSX.utils.aoa_to_sheet(rows);

        const textColumnIndexes = [2, 3, 5, 7, 9, ...(hasSkuPlan ? [15] : [])];
        for (let r = 0; r <= 1000; r++) {
            textColumnIndexes.forEach(c => {
                const ref = XLSX.utils.encode_cell({ r, c });
                ws[ref] = { t: 's', v: ws[ref]?.v ?? '', z: '@' };
            });
        }
        ws[XLSX.utils.encode_cell({ r: 1, c: 4 })].z = 'dd/mm/yyyy';
        ws['!cols'] = [
            { wch: 24 },
            { wch: 36 },
            { wch: 15 },
            { wch: 15 },
            { wch: 16 },
            { wch: 16 },
            { wch: 12 },
            { wch: 12 },
            { wch: 28 },
            { wch: 12 },
            { wch: 16 },
            { wch: 18 },
            { wch: 20 },
            { wch: 10 },
            { wch: 12 },
            ...(hasSkuPlan ? [{ wch: 28 }] : []),
            ...(hasCycle ? [{ wch: 12 }, { wch: 14 }] : []),
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'importacao');

        const instructions = [
            ['A linha 2 da aba "importacao" é apenas um exemplo.'],
            ['Ela usa o domínio @example.com e é ignorada automaticamente na importação.'],
            [''],
            ['Campo', 'Formato esperado', 'Exemplo', 'Obrigatório'],
            ['nome', 'Texto completo', 'Maria da Silva', 'Sim'],
            ['email', 'E-mail válido e único', 'maria.silva.exemplo@example.com', 'Sim'],
            ['CPF', '11 dígitos, preservando zeros à esquerda', '52998224725', 'Sim'],
            [
                'cpf_titular',
                'CPF do titular (mesmo clube). Vazio = titular. Preenchido = dependente (vínculo + assinatura INACTIVE sem plano).',
                '12345678909',
                'Não',
            ],
            ['data_nascimento', 'Data DD/MM/AAAA', '20/05/1990', 'Não'],
            ['telefone', 'DDD + número, somente dígitos', '11987654321', 'Não'],
            ['genero', 'masculino ou feminino', 'feminino', 'Não'],
            ['cep', '8 dígitos, preservando zeros à esquerda', '01310100', 'Não'],
            ['endereco', 'Logradouro', 'Avenida Paulista', 'Não'],
            ['numero', 'Texto ou número', '1000', 'Não'],
            ['complemento', 'Texto', 'Apto 101', 'Não'],
            ['bairro', 'Texto', 'Bela Vista', 'Não'],
            ['cidade', 'Texto', 'São Paulo', 'Não'],
            ['estado', 'Sigla com 2 letras', 'SP', 'Não'],
            ['pais', 'Texto', 'Brasil', 'Não'],
            ...(hasSkuPlan
                ? [
                      [
                          'sku',
                          'SKU do plano (somente titular; dependente acompanha o plano do cpf_titular na leitura)',
                          sampleSku || 'SUBSTITUA_PELO_SKU_DO_PLANO',
                          clubData?.functions?.includes('benefit') ? 'Sim (titular)' : 'Não',
                      ],
                  ]
                : []),
            ...(hasCycle
                ? [
                      [
                          'ciclo',
                          'mensal ou anual (somente titular, junto com o sku). Vazio = mensal no cadastro novo; no existente mantém o ciclo atual. Anual exige valor anual no plano.',
                          'mensal',
                          'Não',
                      ],
                      [
                          'fim_ciclo',
                          'Data DD/MM/AAAA. Para quem já pagou fora da plataforma (ex.: vendido direto no Asaas): o acesso vai até essa data. Exige sku e ciclo; data futura e no máximo 1 ciclo a partir de hoje. Vazio = hoje + 1 ciclo.',
                          '15/03/2027',
                          'Não',
                      ],
                  ]
                : []),
        ];
        const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions);
        instructionsSheet['!cols'] = [{ wch: 20 }, { wch: 42 }, { wch: 40 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, instructionsSheet, 'instrucoes');
        XLSX.writeFile(wb, 'modelo importacao.xlsx');
    };

    const openFilePicker = () => {
        fileInputRef.current?.click();
    };

    const blockingActive =
        hasActiveImports || (liveImport && ['PENDING', 'PROCESSING'].includes(liveImport.status));

    return (
        <>
            <Button type="button" size="sm" onClick={() => setOpen(true)}>
                <UploadIcon className="size-4" />
                Nova importação
            </Button>

            <Dialog open={open} onOpenChange={handleOpenChange}>
                <ScrollDialogContent className="sm:max-w-lg">
                    <ScrollDialogHeader className="flex flex-row items-start justify-between gap-2 pr-12">
                        <div>
                            <DialogTitle>Importar contatos</DialogTitle>
                            <p className="text-sm text-muted-foreground">
                                {showProgress
                                    ? 'Acompanhe o processamento da planilha.'
                                    : 'Envie uma planilha .xlsx com os assinantes.'}
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className="absolute top-4 right-12"
                            aria-label="Ajuda sobre importação"
                            onClick={() => chargeHelp(<ImportHelpContent />)}
                        >
                            <HelpCircleIcon className="size-4" />
                        </Button>
                    </ScrollDialogHeader>

                    <DialogBody>
                        {showProgress ? (
                            <ImportProgressPanel importItem={tracking} connected={connected} />
                        ) : (
                            <div className="flex flex-col gap-4">
                                <Alert>
                                    <CircleAlertIcon />
                                    <AlertTitle>Senha de acesso</AlertTitle>
                                    <AlertDescription>
                                        A senha provisória do assinante são os{' '}
                                        <strong>6 primeiros dígitos do CPF/CNPJ</strong>. Cada cadastro válido dispara um
                                        e-mail de boas-vindas com os dados de acesso.
                                    </AlertDescription>
                                </Alert>

                                <div className="rounded-lg border bg-muted/30 p-4">
                                    <div className="flex flex-col items-center gap-3 text-center">
                                        <p className="font-medium">Baixar planilha modelo</p>
                                        <p className="text-sm text-muted-foreground">
                                            Use sempre este modelo para garantir que sua importação seja processada
                                            corretamente.
                                        </p>
                                        <Button type="button" variant="default" size="sm" onClick={handleDownloadTemplate}>
                                            <DownloadIcon className="size-4" />
                                            Baixar modelo
                                        </Button>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    disabled={blockingActive}
                                    onClick={openFilePicker}
                                    className={cn(
                                        'flex h-28 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-sm text-muted-foreground transition-colors',
                                        blockingActive
                                            ? 'cursor-not-allowed opacity-60'
                                            : 'cursor-pointer hover:border-primary hover:text-foreground',
                                    )}
                                >
                                    <CloudUploadIcon className="size-5" />
                                    {blockingActive
                                        ? 'Upload desabilitado'
                                        : 'Clique aqui para selecionar seu arquivo .xlsx'}
                                </button>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx"
                                    className="hidden"
                                    onChange={handleFileChange}
                                    disabled={blockingActive}
                                />

                                {file ? (
                                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm">
                                        <span className="truncate">Arquivo selecionado: {file.name}</span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={openFilePicker}
                                            disabled={blockingActive}
                                        >
                                            Trocar
                                        </Button>
                                    </div>
                                ) : null}

                                {loading ? (
                                    <div className="flex items-center justify-center gap-2 py-2">
                                        <Spinner className="size-5" />
                                        <span className="text-sm text-muted-foreground">Enviando e validando planilha…</span>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </DialogBody>

                    <ScrollDialogFooter>
                        {showProgress ? (
                            <Button type="button" onClick={() => handleOpenChange(false)} disabled={loading}>
                                Fechar
                            </Button>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleOpenChange(false)}
                                    disabled={loading}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleImport}
                                    disabled={!file || loading || blockingActive}
                                >
                                    {loading ? 'Importando…' : 'Importar'}
                                </Button>
                            </>
                        )}
                    </ScrollDialogFooter>
                </ScrollDialogContent>
            </Dialog>
        </>
    );
}
